import React, { useState, useEffect, useContext } from "react";
import PopoverInput from "./inner/popoverInput.jsx";
import { planeIcon as icon } from "./inner/fieldIcons.jsx";
import PickerFooter from "./inner/pickerFooter.jsx";
import { DataSourceContext, IsMobileContext } from "../context/contexts.jsx";

// The airport panel as in the redesign mockup: airports grouped by region in columns, each with a
// checkbox; a region's heading selects or clears the whole region. Each airport in the departures response
// carries its region and the region's position (group / groupOrder, from iVector airport groups named
// "SF: <region>"); an airport in no region is listed under "Other Airports", last.
const OTHER_REGION = "Other Airports";

// "London Gatwick Airport" -> "London Gatwick", as the mockup lists them.
const displayName = (airport) => (airport.name || airport.shortName || airport.code).replace(/\s+Airport$/i, "");

const groupAirports = (airports) => {
    const byName = new Map();
    for (const airport of airports) {
        const name = airport.group || OTHER_REGION;
        if (!byName.has(name)) {
            byName.set(name, { name, order: airport.group ? (airport.groupOrder ?? Number.MAX_SAFE_INTEGER) : Infinity, airports: [] });
        }
        byName.get(name).airports.push(airport);
    }
    const groups = [...byName.values()].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    for (const group of groups) {
        group.airports.sort((a, b) => displayName(a).localeCompare(displayName(b)));
    }
    return groups;
};

const AirportCheckbox = ({ label, checked, onChange }) => (
    <label className="flex items-center gap-2 py-1 text-sm text-ink cursor-pointer hover:text-navy-700">
        <input type="checkbox" className="oly-form-check shrink-0" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}</span>
    </label>
);

const DeparturePicker = ({ selectedAirports, onUpdate, updateOverviewText, error }) => {
    const [availableDepAirs, setAvailableDepAirs] = useState(null);
    const [filterText, setFilterText] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const dataSource = useContext(DataSourceContext);
    const isMobile = useContext(IsMobileContext);

    useEffect(() => {
        async function fetchData() {
            setAvailableDepAirs(null)
            var departureAirports = await dataSource.getDepartures();
            setAvailableDepAirs(departureAirports);
        }
        fetchData();
    }, []);
    useEffect(() => {
        updateOverviewText(buildDepartureText());
    }, [selectedAirports, availableDepAirs]);

    // Clear the search text on close so the field shows the selection summary.
    useEffect(() => {
        if (!isOpen) {
            setFilterText("");
        }
    }, [isOpen]);

    const setAirport = (code, isSelected) => {
        onUpdate(isSelected ? [...new Set([...selectedAirports, code])] : selectedAirports.filter((item) => item !== code));
    };

    // A region heading: select every airport in it, or clear them all when they are all selected already.
    const toggleRegion = (codes) => {
        const allSelected = codes.every((c) => selectedAirports.includes(c));
        onUpdate(allSelected ? selectedAirports.filter((c) => !codes.includes(c)) : [...new Set([...selectedAirports, ...codes])]);
    };

    const buildDepartureText = () => {
        if (!availableDepAirs) {
            return "Loading...";
        }
        const names = selectedAirports
            .map((code) => availableDepAirs.find((item) => item.code == code))
            .filter(Boolean)
            .map(displayName);
        if (names.length == 0) {
            return "Choose Airport";
        }
        return names.length == 1 ? names[0] : names[0] + " +" + (names.length - 1);
    };

    const query = filterText.trim().toLowerCase();
    const matches = (a) => !query || a.name.toLowerCase().includes(query) || (a.shortName ?? "").toLowerCase().includes(query) || a.code.toLowerCase() == query;
    const groups = availableDepAirs
        ? groupAirports(availableDepAirs)
            .map((g) => ({ ...g, airports: g.airports.filter(matches) }))
            .filter((g) => g.airports.length > 0)
        : [];

    // Mobile search box (desktop types into the field itself).
    const header = isMobile
        ? <input type="text" autoComplete="off" value={filterText} onChange={(e) => setFilterText(e.target.value)} className="oly-form-input" placeholder="Search airports" aria-label="Search airports" />
        : null;

    return <PopoverInput label="Departing From" fieldClass="oly-field-departure" anchor="form" panelMaxHeight={720} error={error} header={header}
        footer={<PickerFooter onClear={() => onUpdate([])} onConfirm={() => setIsOpen(false)} clearDisabled={selectedAirports.length == 0} />}
        icon={icon} inputText={availableDepAirs ? buildDepartureText() : "Choose Airport"} isLoading={availableDepAirs == null}
        onTextUpdate={setFilterText} filterText={filterText} isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-x-8">
            {groups.map((g) => {
                const codes = g.airports.map((a) => a.code);
                const allSelected = codes.every((c) => selectedAirports.includes(c));
                return (
                    <div key={g.name} className="break-inside-avoid mb-5">
                        <button type="button" onClick={() => toggleRegion(codes)} aria-pressed={allSelected}
                            title={allSelected ? "Clear " + g.name : "Select all " + g.name}
                            className="mb-1 text-left text-sm font-semibold text-navy-700 hover:text-sky-600 cursor-pointer">
                            {g.name}
                        </button>
                        {g.airports.map((a) => (
                            <AirportCheckbox key={a.code} label={displayName(a)} checked={selectedAirports.includes(a.code)}
                                onChange={(checked) => setAirport(a.code, checked)} />
                        ))}
                    </div>
                );
            })}
        </div>
        {/* Loaded, but nothing to show: distinguish "no airports at all" from
            "no match for the current search" so a failed/empty fetch doesn't
            look like a broken, empty panel. */}
        {availableDepAirs && availableDepAirs.length == 0 && (
            <div className="text-ink-soft text-sm p-3 text-center">No departure airports available.</div>
        )}
        {availableDepAirs && availableDepAirs.length > 0 && query && groups.length == 0 && (
            <div className="text-ink-soft text-sm p-3 text-center">No airports match &ldquo;{filterText}&rdquo;.</div>
        )}
    </PopoverInput>
}
export default DeparturePicker;
